import { usePatient } from "@/api/Patients";
import { type IPatientType } from "@medoma/models/patients";
import {
  recurringDictionary,
  recurringOptionSchema,
  weekdayDictionary,
} from "@/forms/AddActivityForm/Scheduling/schedulingModels";
import type { IBatchTemplateActivity } from "@/pages/template-manager/BatchTemplateForm/batchTemplateFormSchema";
import { selectOrdinal, t } from "@lingui/core/macro";

export const useMatchPatientTypes = (patientId: string) => {
  const { data: patient } = usePatient(patientId);

  const isPatientTypesMatching = (patientTypes: IPatientType[]) => {
    if (!patient) {
      return false;
    }

    return patientTypes.includes(patient.patientType);
  };

  return { isPatientTypesMatching, patient };
};

export const getRecurrenceLabel = ({
  recurrence,
  customRecurrenceInterval,
  customRecurrenceIntervalUnit,
  weekdays,
}: Pick<
  IBatchTemplateActivity,
  | "recurrence"
  | "customRecurrenceInterval"
  | "customRecurrenceIntervalUnit"
  | "weekdays"
>) => {
  if (!recurrence) {
    return undefined;
  }

  if (recurrence === recurringOptionSchema.enum.custom) {
    if (!customRecurrenceInterval || !customRecurrenceIntervalUnit) {
      return undefined;
    }

    const intervalAsNumber = Number(customRecurrenceInterval);
    if (isNaN(intervalAsNumber) || intervalAsNumber < 1) {
      return undefined;
    }

    if (customRecurrenceIntervalUnit === "weeks") {
      return selectOrdinal(customRecurrenceInterval, {
        1: "Varje vecka",
        2: "Varannan vecka",
        one: "Var #:a vecka",
        two: "Var #:a vecka",
        few: "Var #:e vecka",
        other: "Var #:e vecka",
      });
    }
    if (customRecurrenceIntervalUnit === "days") {
      return selectOrdinal(customRecurrenceInterval, {
        1: "Varje dag",
        2: "Varannan dag",
        one: "Var #:a dag",
        two: "Var #:a dag",
        few: "Var #:e dag",
        other: "Var #:e dag",
      });
    }
  }

  if (
    recurrence === recurringOptionSchema.enum.onSelectedWeekdays &&
    weekdays
  ) {
    if (weekdays?.length === 0) {
      return t`Välj dagar`;
    }
    return weekdays
      .map((day) => t(weekdayDictionary[day].regular).toLocaleLowerCase())
      .join(", ");
  }

  return t(recurringDictionary[recurrence]);
};
